import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  Users, 
  Files, 
  ArrowRight,
  Plus,
  GoogleLogo,
  LinkBreak,
  CheckCircle,
  ArrowsClockwise,
  UserCirclePlus,
  FolderSimple,
  CalendarCheck,
  PlugsConnected,
  Sparkle,
  Pause,
  Play,
  ClipboardText,
  EnvelopeSimple,
  X,
  Warning,
} from '@phosphor-icons/react';
import AdminLayout from '../components/AdminLayout';
import { toast } from 'sonner';

import API_URL from '../config';

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    clients: 0,
    matters: 0,
    messages: 0
  });
  const [recentMatters, setRecentMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [googleStatus, setGoogleStatus] = useState({ connected: false, email: '' });
  const [syncing, setSyncing] = useState(false);
  const [busyAction, setBusyAction] = useState('');
  const [beta, setBeta] = useState(null);
  const [limitInput, setLimitInput] = useState('');
  const [savingBeta, setSavingBeta] = useState(false);
  const [waitlist, setWaitlist] = useState(null); // null=closed, []=open modal
  const [notifying, setNotifying] = useState(false);
  const [searchParams] = useSearchParams();

  const fetchBeta = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/beta-settings`);
      setBeta(data);
      setLimitInput(String(data.limit));
    } catch (e) { /* non-blocking */ }
  };

  useEffect(() => {
    fetchData();
    checkGoogleStatus();
    fetchBeta();
    
    // Check for Google OAuth callback
    if (searchParams.get('google_connected') === 'true') {
      toast.success('Google account connected successfully!');
      checkGoogleStatus();
    }
    if (searchParams.get('google_error')) {
      toast.error(`Google connection failed: ${searchParams.get('google_error')}`);
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [clientsRes, mattersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/clients`),
        axios.get(`${API_URL}/api/admin/matters`)
      ]);

      const clientData = clientsRes.data;
      setStats({
        clients: clientData.length,
        activeClients: clientData.filter(c => c.is_active).length,
        matters: mattersRes.data.length
      });

      // Get recent matters (last 5)
      const sortedMatters = [...mattersRes.data]
        .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
        .slice(0, 5);
      setRecentMatters(sortedMatters);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkGoogleStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/google/status`, {
        
      });
      setGoogleStatus(response.data);
    } catch (error) {
      console.error('Error checking Google status:', error);
    }
  };

  const handleConnectGoogle = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/google/login`, {
        
      });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Failed to start Google connection');
    }
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm('Disconnect Google account? Email and Drive access will be lost.')) return;
    try {
      await axios.post(`${API_URL}/api/google/disconnect`, {});
      setGoogleStatus({ connected: false, email: '' });
      toast.success('Google disconnected');
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleSyncAllTMTKO = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/sync-all-tmtko`, {});
      const { synced, errors, total } = response.data;
      toast.success(`Synced ${synced}/${total} matters${errors > 0 ? ` (${errors} errors)` : ''}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to sync with TM TKO');
    } finally {
      setSyncing(false);
    }
  };

  const handleBackfillContactIds = async () => {
    setBusyAction('backfill');
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/backfill-contact-ids`, {});
      toast.success(
        `Linked ${data.matched} client${data.matched === 1 ? '' : 's'} to HubSpot` +
        (data.folders_set ? ` · ${data.folders_set} folder link${data.folders_set === 1 ? '' : 's'} set` : '') +
        (data.not_found ? ` · ${data.not_found} not found in HubSpot` : '')
      );
      fetchData();
    } catch (error) {
      toast.error('Failed to backfill contact IDs');
    } finally {
      setBusyAction('');
    }
  };

  const handleSyncClientFolders = async () => {
    setBusyAction('folders');
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/sync-client-folders`, {});
      toast.success(`Refreshed client folders · ${data.updated}/${data.processed} updated`);
    } catch (error) {
      toast.error('Failed to refresh client folders');
    } finally {
      setBusyAction('');
    }
  };

  const handleSyncDeadlines = async () => {
    setBusyAction('deadlines');
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/sync-hubspot-deadlines`, {});
      toast.success(`Refreshed deadlines · ${data.updated}/${data.processed} matters updated`);
    } catch (error) {
      toast.error('Failed to refresh deadlines');
    } finally {
      setBusyAction('');
    }
  };

  const handleSaveLimit = async () => {
    const n = parseInt(limitInput, 10);
    if (isNaN(n) || n < 0) { toast.error('Enter a valid number'); return; }
    setSavingBeta(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/admin/beta-settings`, { signup_limit: n });
      setBeta(data);
      toast.success(`Beta limit set to ${data.limit}`);
    } catch (e) {
      toast.error('Failed to update limit');
    } finally {
      setSavingBeta(false);
    }
  };

  const handleToggleSignups = async () => {
    if (!beta) return;
    setSavingBeta(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/admin/beta-settings`, { signups_open: !beta.signups_open });
      setBeta(data);
      toast.success(data.signups_open ? 'Signups resumed' : 'Signups paused');
    } catch (e) {
      toast.error('Failed to update signups');
    } finally {
      setSavingBeta(false);
    }
  };

  const openWaitlist = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/beta-waitlist`);
      setWaitlist(data);
    } catch (e) {
      toast.error('Failed to load waitlist');
    }
  };

  const copyWaitlistEmails = () => {
    if (!waitlist || waitlist.length === 0) return;
    const emails = waitlist.map((w) => w.email).join(', ');
    navigator.clipboard.writeText(emails);
    toast.success(`Copied ${waitlist.length} email${waitlist.length === 1 ? '' : 's'}`);
  };

  const notifyWaitlist = async () => {
    const pending = (waitlist || []).filter((w) => !w.notified).length;
    if (pending === 0) { toast.info('Everyone on the waitlist has already been emailed.'); return; }
    if (!window.confirm(`Send the "a spot just opened" email to ${pending} waitlisted client${pending === 1 ? '' : 's'} who haven't been emailed yet? This sends real emails.`)) return;
    setNotifying(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/notify-waitlist`);
      toast.success(`Sent ${data.sent} email${data.sent === 1 ? '' : 's'}${data.failed ? ` · ${data.failed} failed` : ''}`);
      await openWaitlist();
      fetchBeta();
    } catch (e) {
      toast.error('Failed to email the waitlist');
    } finally {
      setNotifying(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#FFD60A] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="label-uppercase text-[#52525B] mb-1">Admin Panel</p>
            <h1 className="text-3xl font-bold text-[#09090B] tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Dashboard
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleSyncAllTMTKO}
              disabled={syncing}
              className="btn-secondary flex items-center gap-2"
              data-testid="sync-all-tmtko-btn"
            >
              <ArrowsClockwise weight="bold" className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync TM TKO'}
            </button>
            <Link to="/admin/clients" className="btn-secondary flex items-center gap-2" data-testid="add-client-link">
              <Plus weight="bold" className="w-4 h-4" />
              Add Client
            </Link>
            <Link to="/admin/matters" className="btn-primary flex items-center gap-2" data-testid="add-matter-link">
              <Plus weight="bold" className="w-4 h-4" />
              Add Matter
            </Link>
          </div>
        </div>

        {/* Stats */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <motion.div variants={itemVariants}>
            <Link to="/admin/clients" className="card-swiss block" data-testid="stat-clients">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-[#3B82F6]/10 flex items-center justify-center">
                  <Users weight="duotone" className="w-6 h-6 text-[#3B82F6]" />
                </div>
                <ArrowRight weight="bold" className="w-5 h-5 text-[#A1A1AA]" />
              </div>
              <p className="text-sm text-[#52525B] mb-1">Total Clients</p>
              <p className="text-4xl font-bold text-[#09090B]">{stats.clients}</p>
            </Link>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Link to="/admin/matters" className="card-swiss block" data-testid="stat-matters">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-[#FFD60A]/10 flex items-center justify-center">
                  <Files weight="duotone" className="w-6 h-6 text-[#F59E0B]" />
                </div>
                <ArrowRight weight="bold" className="w-5 h-5 text-[#A1A1AA]" />
              </div>
              <p className="text-sm text-[#52525B] mb-1">Active Matters</p>
              <p className="text-4xl font-bold text-[#09090B]">{stats.matters}</p>
            </Link>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Link to="/admin/clients" className="card-swiss block" data-testid="stat-active-clients">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-[#22C55E]/10 flex items-center justify-center">
                  <CheckCircle weight="duotone" className="w-6 h-6 text-[#22C55E]" />
                </div>
                <ArrowRight weight="bold" className="w-5 h-5 text-[#A1A1AA]" />
              </div>
              <p className="text-sm text-[#52525B] mb-1">Active Clients</p>
              <p className="text-4xl font-bold text-[#09090B]">{stats.activeClients} <span className="text-lg text-[#A1A1AA] font-normal">/ {stats.clients}</span></p>
            </Link>
          </motion.div>
        </motion.div>

        {/* Google Integration Card */}
        <div className="card-swiss" data-testid="google-integration-card">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 flex items-center justify-center ${googleStatus.connected ? 'bg-[#22C55E]/10' : 'bg-[#F4F4F5]'}`}>
                <GoogleLogo weight="bold" className={`w-6 h-6 ${googleStatus.connected ? 'text-[#22C55E]' : 'text-[#A1A1AA]'}`} />
              </div>
              <div>
                <h3 className="font-semibold text-[#09090B]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  Google Integration
                </h3>
                {googleStatus.connected ? (
                  <p className="text-sm text-[#52525B] flex items-center gap-1">
                    <CheckCircle weight="fill" className="w-4 h-4 text-[#22C55E]" />
                    Connected as {googleStatus.email}
                  </p>
                ) : googleStatus.needs_reconnect ? (
                  <p className="text-sm text-red-600 flex items-center gap-1" data-testid="google-needs-reconnect">
                    <Warning weight="fill" className="w-4 h-4" />
                    Google rejected the saved sign-in for {googleStatus.email}. Drive reports can't be read until you reconnect.
                  </p>
                ) : (
                  <p className="text-sm text-[#52525B]">
                    Connect Gmail & Drive to view client email threads and documents
                  </p>
                )}
              </div>
            </div>
            {googleStatus.connected ? (
              <button
                onClick={handleDisconnectGoogle}
                className="btn-secondary flex items-center gap-2 text-red-500 border-red-200 hover:bg-red-50"
                data-testid="disconnect-google-btn"
              >
                <LinkBreak weight="bold" className="w-4 h-4" />
                Disconnect
              </button>
            ) : (
              <button
                onClick={handleConnectGoogle}
                className="btn-primary flex items-center gap-2"
                data-testid="connect-google-btn"
              >
                <GoogleLogo weight="bold" className="w-4 h-4" />
                {googleStatus.needs_reconnect ? 'Reconnect Google' : 'Connect Google'}
              </button>
            )}
          </div>
        </div>

        {/* Beta Access */}
        {beta && (
          <div className="card-swiss" data-testid="beta-access-card">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#FFD60A]/15 flex items-center justify-center flex-shrink-0">
                  <Sparkle weight="duotone" className="w-6 h-6 text-[#141b4d]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#09090B]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                    Beta Access
                  </h3>
                  <p className="text-sm text-[#52525B]">
                    <span className="font-semibold text-[#09090B]" data-testid="beta-taken">{beta.taken}</span> of {beta.limit} spots used ·{' '}
                    <span className="font-semibold text-[#09090B]" data-testid="beta-remaining">{beta.remaining}</span> remaining ·{' '}
                    <span className={`font-semibold ${beta.open ? 'text-[#006747]' : 'text-[#b91c1c]'}`} data-testid="beta-open-state">
                      {beta.signups_open ? (beta.remaining > 0 ? 'Open' : 'Full') : 'Paused'}
                    </span>
                  </p>
                </div>
              </div>
              <button
                onClick={handleToggleSignups}
                disabled={savingBeta}
                className={`btn-secondary flex items-center gap-2 ${beta.signups_open ? 'text-[#b91c1c] border-red-200 hover:bg-red-50' : 'text-[#006747] border-green-200 hover:bg-green-50'}`}
                data-testid="beta-toggle-signups-btn"
              >
                {beta.signups_open ? <><Pause weight="fill" className="w-4 h-4" /> Pause Signups</> : <><Play weight="fill" className="w-4 h-4" /> Resume Signups</>}
              </button>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-end">
              <div>
                <label className="label-uppercase block mb-2">Signup limit</label>
                <input
                  type="number"
                  min="0"
                  value={limitInput}
                  onChange={(e) => setLimitInput(e.target.value)}
                  className="input-swiss w-full sm:w-40"
                  data-testid="beta-limit-input"
                />
              </div>
              <button onClick={handleSaveLimit} disabled={savingBeta} className="btn-primary" data-testid="beta-save-limit-btn">
                {savingBeta ? 'Saving…' : 'Save Limit'}
              </button>
              <button onClick={openWaitlist} className="btn-secondary flex items-center gap-2 sm:ml-auto" data-testid="beta-view-waitlist-btn">
                <ClipboardText weight="duotone" className="w-4 h-4" />
                Waitlist ({beta.waitlist_count})
              </button>
            </div>
          </div>
        )}

        {/* Sync from HubSpot */}
        <div className="card-swiss" data-testid="hubspot-sync-card">
          <div className="flex items-center gap-4 mb-1">
            <div className="w-12 h-12 bg-[#FF7A59]/10 flex items-center justify-center flex-shrink-0">
              <PlugsConnected weight="duotone" className="w-6 h-6 text-[#FF7A59]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#09090B]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Sync from HubSpot
              </h3>
              <p className="text-sm text-[#52525B]">
                These run automatically twice a day — use these buttons to refresh right now. Read-only from HubSpot; no client emails are sent.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
            {/* Backfill contact IDs */}
            <div className="p-4 bg-[#F4F4F5] flex flex-col">
              <div className="flex items-center gap-2 mb-1">
                <UserCirclePlus weight="duotone" className="w-5 h-5 text-[#141b4d]" />
                <p className="font-semibold text-sm text-[#09090B]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Link Clients to HubSpot</p>
              </div>
              <p className="text-xs text-[#52525B] mb-3 flex-1">
                One-time: match clients to their HubSpot contact by email so they're included in the daily folder sync.
              </p>
              <button
                onClick={handleBackfillContactIds}
                disabled={busyAction !== ''}
                className="btn-secondary flex items-center justify-center gap-2 text-sm"
                data-testid="backfill-contact-ids-btn"
              >
                <ArrowsClockwise weight="bold" className={`w-4 h-4 ${busyAction === 'backfill' ? 'animate-spin' : ''}`} />
                {busyAction === 'backfill' ? 'Linking…' : 'Run Backfill'}
              </button>
            </div>

            {/* Refresh client folders */}
            <div className="p-4 bg-[#F4F4F5] flex flex-col">
              <div className="flex items-center gap-2 mb-1">
                <FolderSimple weight="duotone" className="w-5 h-5 text-[#141b4d]" />
                <p className="font-semibold text-sm text-[#09090B]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Refresh Client Folders</p>
              </div>
              <p className="text-xs text-[#52525B] mb-3 flex-1">
                Pull the latest Drive folder link from each client's HubSpot <code>client_folder_url</code> property.
              </p>
              <button
                onClick={handleSyncClientFolders}
                disabled={busyAction !== ''}
                className="btn-secondary flex items-center justify-center gap-2 text-sm"
                data-testid="sync-client-folders-btn"
              >
                <ArrowsClockwise weight="bold" className={`w-4 h-4 ${busyAction === 'folders' ? 'animate-spin' : ''}`} />
                {busyAction === 'folders' ? 'Refreshing…' : 'Refresh Folders'}
              </button>
            </div>

            {/* Refresh deadlines */}
            <div className="p-4 bg-[#F4F4F5] flex flex-col">
              <div className="flex items-center gap-2 mb-1">
                <CalendarCheck weight="duotone" className="w-5 h-5 text-[#141b4d]" />
                <p className="font-semibold text-sm text-[#09090B]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Refresh Deadlines</p>
              </div>
              <p className="text-xs text-[#52525B] mb-3 flex-1">
                Pull the latest OA / SOU / publication dates from HubSpot tickets so client countdowns update.
              </p>
              <button
                onClick={handleSyncDeadlines}
                disabled={busyAction !== ''}
                className="btn-secondary flex items-center justify-center gap-2 text-sm"
                data-testid="sync-deadlines-btn"
              >
                <ArrowsClockwise weight="bold" className={`w-4 h-4 ${busyAction === 'deadlines' ? 'animate-spin' : ''}`} />
                {busyAction === 'deadlines' ? 'Refreshing…' : 'Refresh Deadlines'}
              </button>
            </div>
          </div>
        </div>

        <div className="card-swiss">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-[#09090B]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Recent Matters
            </h2>
            <Link to="/admin/matters" className="text-sm text-[#52525B] hover:text-[#09090B] flex items-center gap-1">
              View All <ArrowRight weight="bold" className="w-4 h-4" />
            </Link>
          </div>

          {recentMatters.length === 0 ? (
            <p className="text-[#52525B] text-center py-8">No matters yet. Create your first client and matter to get started.</p>
          ) : (
            <div className="space-y-3">
              {recentMatters.map((matter) => (
                <div
                  key={matter.id}
                  className="flex items-center justify-between p-4 bg-[#F4F4F5] hover:bg-[#E4E4E7] transition-colors"
                  data-testid={`recent-matter-${matter.id}`}
                >
                  <div>
                    <p className="font-medium text-[#09090B]">{matter.trademark_name}</p>
                    <p className="text-sm text-[#52525B]">
                      {matter.status_phase.charAt(0).toUpperCase() + matter.status_phase.slice(1)} • Updated {new Date(matter.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`status-badge status-${matter.status_phase}`}>
                    {matter.status_phase}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Waitlist modal */}
      {waitlist !== null && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40" onClick={() => setWaitlist(null)} data-testid="waitlist-modal">
          <div className="bg-white w-full max-w-lg max-h-[80vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4" style={{ backgroundColor: '#141b4d' }}>
              <h3 className="text-white font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Beta Waitlist ({waitlist.length})</h3>
              <button onClick={() => setWaitlist(null)} className="text-white/80 hover:text-white" data-testid="waitlist-modal-close"><X weight="bold" className="w-5 h-5" /></button>
            </div>
            <div className="p-5 overflow-y-auto">
              {waitlist.length === 0 ? (
                <p className="text-[#52525B] text-center py-6">No one on the waitlist yet.</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <button onClick={notifyWaitlist} disabled={notifying} className="btn-primary flex items-center gap-2" data-testid="waitlist-notify-btn">
                      <EnvelopeSimple weight="bold" className="w-4 h-4" />
                      {notifying ? 'Sending…' : 'Email the waitlist (spot opened)'}
                    </button>
                    <button onClick={copyWaitlistEmails} className="btn-secondary flex items-center gap-2" data-testid="waitlist-copy-emails-btn">
                      <ClipboardText weight="duotone" className="w-4 h-4" /> Copy all emails
                    </button>
                  </div>
                  <p className="text-xs mb-4" style={{ color: '#9a9488' }}>
                    Emails go only to people who haven't been notified yet. Already-emailed clients show an "emailed" badge.
                  </p>
                  <div className="space-y-2">
                    {waitlist.map((w, i) => (
                      <div key={w.email} className="flex items-center justify-between p-3 bg-[#F4F4F5] text-sm" data-testid={`waitlist-row-${i}`}>
                        <div>
                          <p className="font-medium text-[#09090B]">{w.name || '—'}</p>
                          <p className="text-[#52525B]">{w.email}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          {w.notified && (
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5" style={{ backgroundColor: '#dcfce7', color: '#14532d' }}>Emailed</span>
                          )}
                          <span className="text-xs text-[#9a9488]">{w.created_at ? new Date(w.created_at).toLocaleDateString() : ''}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
